import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import { LegalDocumentModal } from "./LegalDocumentModal";
import { ArrowRightIOS } from "../../../../assets/icons";
import useWindowDimensions from "../../../../hooks/use-window-dimensions";
import { useTenant } from "../../../../tenant/branding/useTenant";
import styles from "./legal-page.module.scss";

type LegalDocument = "privacy" | "terms";

interface LegalItemProps {
	title: string;
	description: string;
	isFirst?: boolean;
	isLast?: boolean;
	onClick?: () => void;
}

const LegalItem: React.FC<LegalItemProps> = ({ title, description, isFirst, isLast, onClick }) => (
	<button type="button" className={`${styles.item} ${isFirst ? styles.itemFirst : ""} ${isLast ? styles.itemLast : ""}`} onClick={onClick}>
		<div className={styles.itemContent}>
			<span className={styles.itemTitle}>{title}</span>
			<span className={styles.itemDescription}>{description}</span>
		</div>
		<span className={styles.itemChevron}>
			<ArrowRightIOS />
		</span>
	</button>
);

export const LegalPage: React.FC = () => {
	const { t, i18n } = useTranslation();
	const [openDocument, setOpenDocument] = useState<LegalDocument | null>(null);
	const { isMobile } = useWindowDimensions();
	const brand = useTenant();

	// Same documents and placeholder values as the mobile app's legal pages, including
	// its effective date of today.
	const effectiveDate = new Date().toLocaleDateString(i18n.language, { year: "numeric", month: "long", day: "numeric" });
	const contact = brand.legalContact;
	const privacyPolicyText = t("account.privacyPolicyText", {
		effectiveDate,
		appName: brand.name,
		companyName: brand.name,
		contactEmail: contact?.complaintEmail ?? "",
	});
	const termsOfUseText = t("account.termsOfUseText", {
		effectiveDate,
		appName: brand.name,
		companyName: brand.name,
		registeredAddress: contact?.address ?? "",
		contactEmail: contact?.email ?? "",
		phoneNumber: contact?.phones.join("\n") ?? "",
	});

	// On mobile a long legal document opens as a full-screen page (native back, full reading
	// space) instead of a modal. On web the original modal is kept (handled in the main return).
	if (openDocument && isMobile) {
		const isPrivacy = openDocument === "privacy";
		return (
			<CustomerPageLayout
				title={isPrivacy ? t("account.privacyPolicy") : t("account.termsOfService")}
				onBack={() => setOpenDocument(null)}
				isScrollable
			>
				<div className={styles.documentView}>
					<div className={styles.document}>{isPrivacy ? privacyPolicyText : termsOfUseText}</div>
				</div>
			</CustomerPageLayout>
		);
	}

	return (
		<CustomerPageLayout title={t("account.legal")} showBackButton decreaseMainContentPadding>
			<div className={styles.card}>
				<LegalItem
					title={t("account.privacyPolicy")}
					description={t("account.privacyPolicyDescription")}
					isFirst
					onClick={() => setOpenDocument("privacy")}
				/>
				<hr className={styles.divider} />
				<LegalItem
					title={t("account.termsOfService")}
					description={t("account.termsOfServiceDescription")}
					isLast
					onClick={() => setOpenDocument("terms")}
				/>
			</div>

			{openDocument === "privacy" && (
				<LegalDocumentModal title={t("account.privacyPolicy")} onClose={() => setOpenDocument(null)}>
					{privacyPolicyText}
				</LegalDocumentModal>
			)}

			{openDocument === "terms" && (
				<LegalDocumentModal title={t("account.termsOfService")} onClose={() => setOpenDocument(null)}>
					{termsOfUseText}
				</LegalDocumentModal>
			)}
		</CustomerPageLayout>
	);
};

export default LegalPage;
